import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Button, IconButton,
  Paper, Chip, CircularProgress, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel, Tooltip,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Translate as TranslateIcon,
  VolumeUp as AudioIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';

const TOTAL_LANGUAGES = 9; // Total supported languages

const StoryList = () => {
  const navigate = useNavigate();

  // State
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pathFilter, setPathFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // published, draft, all
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [storyToDelete, setStoryToDelete] = useState(null);

  // Load data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Fetch all stories in a single call (no N+1 over paths)
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.admin.getAllStories();
      setStories(response.data.stories || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Derive the path filter options from the flat story list
  const paths = Array.from(
    stories.reduce((map, s) => {
      if (s.path_id && !map.has(s.path_id)) {
        map.set(s.path_id, { path_id: s.path_id, path_title: s.path_title });
      }
      return map;
    }, new Map()).values()
  ).sort((a, b) => (a.path_title || '').localeCompare(b.path_title || ''));

  // Handle search
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Handle filters
  const handlePathFilterChange = (event) => {
    setPathFilter(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const clearFilters = () => {
    setPathFilter('');
    setStatusFilter('');
    setSearchTerm('');
  };

  // Filter stories based on search and filters
  const filteredStories = stories.filter(story => {
    const matchesSearch = (story.title || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesPath = pathFilter ? story.path_id === pathFilter : true;

    const matchesStatus = statusFilter ?
      (statusFilter === 'published' ? story.is_published : !story.is_published) :
      true;

    return matchesSearch && matchesPath && matchesStatus;
  });

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Handle delete
  const openDeleteDialog = (story) => {
    setStoryToDelete(story);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setStoryToDelete(null);
  };

  const confirmDelete = async () => {
    if (!storyToDelete) return;

    try {
      await apiClient.admin.deleteStory(storyToDelete.story_id);
      setStories(prevStories => prevStories.filter(story => story.story_id !== storyToDelete.story_id));
      closeDeleteDialog();
    } catch (error) {
      console.error('Error deleting story:', error);
    }
  };

  // Translation status chip color based on coverage
  const translationColor = (count) => {
    if (!count) return 'default';
    if (count >= TOTAL_LANGUAGES) return 'success';
    return 'warning';
  };

  // Audio status chip color based on coverage vs translations
  const audioColor = (audio, translations) => {
    if (!translations) return 'default';
    if (!audio) return 'error';
    if (audio >= translations) return 'success';
    return 'warning';
  };

  const hasFilters = Boolean(searchTerm || pathFilter || statusFilter);

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3, gap: 1 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Stories
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {loading
              ? 'Loading…'
              : `${filteredStories.length} of ${stories.length} stor${stories.length === 1 ? 'y' : 'ies'}`}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/stories/new')}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          New Story
        </Button>
      </Stack>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search stories"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearSearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="path-filter-label">Path</InputLabel>
              <Select
                labelId="path-filter-label"
                value={pathFilter}
                label="Path"
                onChange={handlePathFilterChange}
              >
                <MenuItem value="">All Paths</MenuItem>
                {paths.map(path => (
                  <MenuItem key={path.path_id} value={path.path_id}>
                    {path.path_title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              disabled={!hasFilters}
              fullWidth
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Stories Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredStories.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No stories found.
          </Typography>
          {hasFilters ? (
            <Button
              variant="outlined"
              size="small"
              onClick={clearFilters}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Clear all filters
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/stories/new')}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Create your first story
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Story</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Path</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Order</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Difficulty</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Translations</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Audio</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Updated</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStories.map((story) => (
                <TableRow
                  key={story.story_id}
                  hover
                  onClick={() => navigate(`/admin/stories/${story.story_id}/translations`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {story.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {story.path_title || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">
                      #{story.order_index}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">
                      {story.difficulty_rating != null ? `${story.difficulty_rating}/5` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<TranslateIcon sx={{ fontSize: 16 }} />}
                      label={story.translation_count || 0}
                      size="small"
                      variant="outlined"
                      color={translationColor(story.translation_count || 0)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={<AudioIcon sx={{ fontSize: 16 }} />}
                      label={story.audio_count || 0}
                      size="small"
                      variant="outlined"
                      color={audioColor(story.audio_count || 0, story.translation_count || 0)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {story.is_published ? (
                      <Chip label="Published" color="success" size="small" variant="outlined" />
                    ) : (
                      <Chip label={story.status || 'Draft'} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={formatDate(story.updated_at)}>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(story.updated_at)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); openDeleteDialog(story); }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Story</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the story "{storyToDelete?.title}"?
          </Typography>
          <Typography color="error" sx={{ mt: 2 }}>
            This will delete all translations and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" sx={{ textTransform: 'none', borderRadius: 2 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoryList;
